import type {
  ImpersonatorDto,
  LoginInput,
  RegisterInput,
  SessionDto,
  UserDto,
} from '@room-mates/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type Api, createApi } from '../api/api.js';
import { createHttpClient } from '../api/httpClient.js';
import { sessionStore } from './sessionStorage.js';

export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: UserDto | null;
  /** Set while an administrator is acting as somebody else. */
  readonly impersonatedBy: ImpersonatorDto | null;
  /** True only for an administrator acting as themselves. */
  readonly canAdminister: boolean;
  readonly api: Api;
  signIn(input: LoginInput): Promise<void>;
  signUp(input: RegisterInput): Promise<void>;
  signOut(): void;
  impersonate(userId: string): Promise<void>;
  stopImpersonating(): void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Owns the session and the API client that carries it.
 *
 * The token is ordinary state, so the client is rebuilt the moment it changes
 * and every request afterwards carries the right credentials. Impersonation is
 * simply a second token: the administrator's own is set aside and put back when
 * they stop, so ending a borrowed session never means signing in again.
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [token, setToken] = useState<string | null>(() => sessionStore.read());
  const [session, setSession] = useState<SessionDto | null>(null);
  const queryClient = useQueryClient();

  // A token with no session behind it yet means we are still checking it.
  const status: AuthStatus =
    token === null ? 'anonymous' : session === null ? 'restoring' : 'authenticated';

  const clearSession = useCallback(() => {
    sessionStore.clear();
    setToken(null);
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const api = useMemo(
    () =>
      createApi(
        createHttpClient({
          getToken: () => token,
          onUnauthorized: () => {
            // Only tear the session down if we thought we had one.
            if (token !== null) {
              clearSession();
            }
          },
        }),
      ),
    [clearSession, token],
  );

  // A stored token may have expired while the tab was closed, and a borrowed
  // one may have been revoked; confirm before rendering as signed in.
  useEffect(() => {
    if (token === null || session !== null) {
      return;
    }
    let cancelled = false;
    api.auth
      .me()
      .then((restored) => {
        if (!cancelled) {
          setSession(restored);
        }
      })
      .catch(() => {
        if (!cancelled) {
          clearSession();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, clearSession, session, token]);

  const value = useMemo<AuthContextValue>(() => {
    const startSession = (issued: { token: string; user: UserDto }): void => {
      sessionStore.write(issued.token);
      setToken(issued.token);
      setSession({ user: issued.user, impersonatedBy: null });
    };

    return {
      status,
      user: session?.user ?? null,
      impersonatedBy: session?.impersonatedBy ?? null,
      canAdminister: session?.user.role === 'admin' && session.impersonatedBy === null,
      api,
      signIn: async (input) => startSession(await api.auth.login(input)),
      signUp: async (input) => startSession(await api.auth.register(input)),
      signOut: clearSession,

      impersonate: async (userId) => {
        const borrowed = await api.admin.impersonate({ userId });
        const ownToken = token;
        if (ownToken === null) {
          return;
        }
        sessionStore.startImpersonation(ownToken, borrowed.token);
        setToken(borrowed.token);
        // Cleared rather than assumed: the server decides what a borrowed
        // session may see, and `/auth/me` is what says so.
        setSession(null);
        queryClient.clear();
      },

      stopImpersonating: () => {
        const restored = sessionStore.endImpersonation();
        setToken(restored);
        setSession(null);
        queryClient.clear();
      },
    };
  }, [api, clearSession, queryClient, session, status, token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
